import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Input, Label } from '../Form';
import { gql, useMutation } from '@apollo/client';
import useNotify from '../../hooks/useNotify';
import FormRow from '../Form/FormRow';
import FormColumn from '../Form/FormColumn';
import Form from '../Form/Form';
import useFileUpload from '../../hooks/useFileUpload';
import ButtonToggle from '../Button/ButtonToggle';
import { Gauge } from '../Tester/types';
import UploadSelectPreview from '../File/UploadSelectPreview';
import { TempFile } from '../../hooks/useUploadTempFile';
import { useMode } from '../../hooks/useMode';
import { Flex } from 'rebass';

type ModalColumnProps = {
  onClose: () => void;
  assignGauge?: (gauge: Gauge) => void;
} & (
  | {
      action: 'create';
      gauge?: Partial<GaugeInput>;
    }
  | {
      action: 'edit';
      gauge: GaugeInput & Gauge;
    }
);

const CREATE_GAUGE = gql`
  mutation CreateGauge($input: GaugeInput!) {
    createGauge(input: $input) {
      id
      isDefault
      gaugeNumber
      calibrationDate
    }
  }
`;

const UPDATE_GAUGE = gql`
  mutation UpdateGauge($gaugeId: ID!, $input: GaugeInput!) {
    updateGauge(gaugeId: $gaugeId, input: $input) {
      id
      isDefault
      gaugeNumber
      calibrationDate
    }
  }
`;

const ARCHIVE_GAUGE = gql`
  mutation ArchiveGauge($gaugeId: ID!) {
    archiveGauge(gaugeId: $gaugeId) {
      id
      gaugeNumber
      calibrationDate
      isDefault
    }
  }
`;

type GaugeInput = {
  id?: string;
  profileId?: string;
  gaugeNumber: string;
  calibrationDate: string;
  isDefault: boolean;
};

const GaugeEditModal: React.FC<ModalColumnProps> = ({
  onClose,
  gauge,
  action,
  assignGauge,
}) => {
  const { showError, showSuccess } = useNotify();
  const mode = useMode();

  const [gaugeInput, setGaugeInput] = useState<GaugeInput>({
    profileId: gauge?.profileId ?? '',
    gaugeNumber: gauge?.gaugeNumber ?? '',
    calibrationDate: gauge?.calibrationDate ?? '',
    isDefault: !!gauge?.isDefault,
  });

  const accept = {
    'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.tiff'],
    'application/pdf': ['.pdf'],
  };
  const dropZone = useFileUpload({
    multiple: false,
    accept,
  });
  const { doUpload, acceptedFile, loading: uploadLoading } = dropZone;

  const onInputChange = useCallback((e: any) => {
    const { name } = e.target;
    let { value } = e.target;
    if (['true', 'false'].includes(value)) {
      value = value === 'true';
    }

    setGaugeInput((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const [createGauge, { loading: createLoading }] = useMutation<
    { createGauge: GaugeInput },
    { input: Omit<GaugeInput, 'id'> & { tempKey: string } }
  >(CREATE_GAUGE, {
    onError: (error) => {
      showError('Error creating gauge', error);
      console.error('Error creating gauge', error);
    },
  });

  const [updateGauge, { loading: updateLoading }] = useMutation<
    { updateGauge: GaugeInput },
    { gaugeId: string; input: Omit<GaugeInput, 'id'> & { tempKey?: string } }
  >(UPDATE_GAUGE, {
    onError: (error) => {
      showError('Error updating gauge', error);
      console.error('Error updating gauge', error);
    },
  });

  const [archiveGauge, { loading: archiveLoading }] = useMutation(
    ARCHIVE_GAUGE,
    {
      variables: { gaugeId: gauge?.id },
      onError: (error) => {
        showError('Error archiving gauge', error);
        console.error('Error archiving gauge', error);
      },
      onCompleted: () => {
        showSuccess('Gauge archived');
        onClose();
      },
    }
  );

  const save = useCallback(async () => {
    if (!acceptedFile && action === 'create') {
      showError('Please provide a gauge calibration image');
      return;
    }

    let tempFile: TempFile | undefined;
    if (acceptedFile) {
      try {
        tempFile = (await doUpload())[0];
      } catch (e) {
        showError('Error uploading file', e as Error);
        return;
      }
    }

    // TODO: The schema def for an individual gauge input doesn't match company?
    // should those align?
    if (mode === 'individual') {
      delete gaugeInput.profileId;
    }

    switch (action) {
      case 'create':
        await createGauge({
          variables: {
            input: { ...gaugeInput, tempKey: tempFile?.key as string },
          },
        })
          .then(async (result) => {
            if (result.data && result.data.createGauge) {
              showSuccess('New gauge was successfully saved');
              if (assignGauge) {
                const newGauge = result.data.createGauge as Gauge;
                assignGauge(newGauge);
              }
              onClose();
            }
          })
          .catch((error) => {
            showError('Something went wrong', error);
            console.error(error.errors);
          });
        break;
      case 'edit':
        await updateGauge({
          variables: {
            gaugeId: gauge.id as string,
            input: { ...gaugeInput, tempKey: tempFile?.key },
          },
        })
          .then(async (result) => {
            if (result.data && result.data.updateGauge) {
              // Do something with data here?
              showSuccess('The gauge was successfully updated!');
              onClose();
            }
          })
          .catch((error) => {
            showError('Something went wrong', error);
            console.error(error.errors);
          });
        break;
    }
  }, [
    action,
    gauge?.id,
    gaugeInput,
    createGauge,
    updateGauge,
    acceptedFile,
    doUpload,
  ]);

  const archive = useCallback(() => {
    if (confirm('Are you sure you want to archive this gauge?')) {
      archiveGauge();
    }
  }, [archiveGauge]);

  const isSaving =
    createLoading || updateLoading || uploadLoading || archiveLoading;

  return (
    <Modal
      title={action === 'edit' ? 'Edit Gauge' : 'Add Gauge'}
      onClose={onClose}
    >
      <ModalBody>
        <Form>
          <FormRow>
            <FormColumn>
              <FormColumn>
                <Label>Gauge Number</Label>
                <Input
                  required
                  name="gaugeNumber"
                  type="text"
                  autoComplete="off"
                  width="100%"
                  value={gaugeInput.gaugeNumber}
                  autoFocus
                  onChange={onInputChange}
                />
              </FormColumn>
              <FormColumn>
                <Label>Is Default</Label>
                <ButtonToggle
                  name="isDefault"
                  value={gaugeInput.isDefault}
                  onClick={onInputChange}
                />
              </FormColumn>
              <FormColumn>
                <Label>Calibration Date</Label>
                <Input
                  required
                  name="calibrationDate"
                  type="date"
                  autoComplete="off"
                  value={gaugeInput.calibrationDate}
                  onChange={onInputChange}
                />
              </FormColumn>
            </FormColumn>
            <FormColumn>
              <UploadSelectPreview
                accept={accept}
                existingFile={action === 'edit' ? gauge.image : undefined}
                uploadState={dropZone}
              />
            </FormColumn>
          </FormRow>
        </Form>
      </ModalBody>
      <ModalFooter>
        {action === 'edit' && (
          <Button
            alignSelf="flex-start"
            onClick={archive}
            disabled={archiveLoading}
            variant="danger"
            style={{ marginRight: 8 }}
          >
            Archive
          </Button>
        )}
        <Flex flexGrow={1} justifyContent="flex-end" sx={{ gap: 2 }}>
          <Button icon="save" onClick={save} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            variant="outline"
            icon="slash-circle"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

GaugeEditModal.propTypes = {
  onClose: PropTypes.any,
  gauge: PropTypes.any,
  action: PropTypes.any.isRequired,
};

export default GaugeEditModal;
